import { PLATFORM_WALKTHROUGH_VIDEO_URL } from '@aibees/elephant/constants';
import { useBootstrapApi } from '@aibees/elephant/hooks/bootstrap/use-bootstrap-api';
import { Grid2, Icon, VideoModal } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames, fHumanReadableNumber } from '@aibees/shared/utils';
import {
  Avatar,
  ButtonBase,
  Card,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { PreviewTotalDataProps as Props } from './types';

export const PreviewTotalData = memo(({ className }: Props) => {
  const { data } = useBootstrapApi();
  const totalPeople = fHumanReadableNumber(data?.person.count.total);
  const totalCompany = fHumanReadableNumber(data?.company.count.total);

  const videoState = useBoolean();

  const renderStatCard = (
    iconName: 'business' | 'people' | 'earth' | 'refresh-outline',
    label: string,
    value: string | undefined,
    isStatic = false
  ) => (
    <Card variant="elevation" sx={SX.card}>
      <Stack direction="row" gap={0.25} alignItems="center" mb={0.5}>
        <Icon name={iconName} size={16} color="primary.500" />
        <Typography
          variant="body5"
          fontWeight={500}
          color="grey.500"
          sx={SX.cardLabel}
        >
          {label}
        </Typography>
      </Stack>
      {isStatic || value ? (
        <Typography variant="body1" fontWeight={400} color="grey.800">
          {value}
        </Typography>
      ) : (
        <Skeleton animation="wave" variant="rounded" sx={SX.skeleton} />
      )}
    </Card>
  );

  return (
    <Styled.Wrapper
      className={classnames('PreviewTotalData', className)}
      container
      sx={SX.root}
      columns={48}
      spacing={1.5}
    >
      <Grid2 xs={48} md={34} flexDirection="column" gap={1.5}>
        <Stack gap={1}>
          <Stack direction="row" gap={1.25} alignItems="center">
            <Avatar
              src="/assets/images/ark-logo.svg"
              alt="logo"
              variant="rounded"
              size={32}
            />
            <Typography variant="body1" fontWeight={700} sx={SX.title}>
              Open the Ark: Discover Your Data Potential
            </Typography>
          </Stack>
          <Typography variant="body4" fontWeight={400} color="primary.700">
            Find the perfect tools to navigate your data landscape, enrich your
            insights, and prospect with precision.
          </Typography>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap">
          {renderStatCard('business', 'Global Companies', totalCompany)}
          {renderStatCard('people', 'Global Contacts', totalPeople)}
          {renderStatCard('earth', 'Global Coverage', '+190 Countries', true)}
          {renderStatCard(
            'refresh-outline',
            'Refresh Cycle',
            'Every 30 Days',
            true
          )}
        </Stack>
      </Grid2>

      <Grid2 xs={48} md={14} alignItems="center">
        <ButtonBase sx={SX.videoTour} onClick={videoState.onTrue}>
          <Stack sx={SX.videoTourIconBox}>
            <Icon name="play-circle-outline" size={32} color="common.white" />
          </Stack>
          <Stack
            flex={1}
            gap={0.5}
            justifyContent="center"
            px={1.5}
            py={1.5}
            minWidth={0}
          >
            <Typography
              variant="body5"
              fontWeight={500}
              color="primary.500"
              sx={SX.videoTourLabel}
            >
              VIDEO TOUR
            </Typography>
            <Typography variant="body2" fontWeight={700} color="primary.600">
              Platform Walkthrough
            </Typography>
            <Typography
              variant="body5"
              fontWeight={500}
              color="grey.700"
              sx={SX.videoTourLabel}
              noWrap
            >
              The fastest way to understand what we do.
            </Typography>
          </Stack>
        </ButtonBase>
      </Grid2>

      <VideoModal
        state={videoState}
        videoUrl={PLATFORM_WALKTHROUGH_VIDEO_URL}
        title="Platform Walkthrough"
      />
    </Styled.Wrapper>
  );
});

PreviewTotalData.displayName = 'PreviewTotalData';
